import { useMemo, useState } from "react";
import { ChevronDown, Film, Image as ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAdsWorkspaceStore, type GeneratedAd } from "@/features/ads";

/** Horizontal thumbnail strip showing images (Banan) or videos (Vid / Vidzz)
 *  produced by the currently-active managed session. Binding is preferred via
 *  `metadata.sessionId` stamped at generation time; legacy records fall back
 *  to a timestamp window derived from the session list (newest-first).
 *
 *  Collapsed by default: assets scroll horizontally in a single row.
 *  Expanded: assets wrap into a multi-row grid so every asset is visible. */
export function SubAgentSessionAssetsStrip({
  agentId,
}: {
  agentId: "banan" | "vid" | "higgsfield" | "higgsfield_cli" | "agent_v";
}) {
  const generatedAds = useAdsWorkspaceStore((s) => s.generatedAds);
  const sessionList = useAdsWorkspaceStore((s) => s.subAgentSessionList[agentId]);
  const activeSessionId = useAdsWorkspaceStore((s) => s.subAgentChats[agentId].sessionId);
  const requestChatImageAttach = useAdsWorkspaceStore((s) => s.requestChatImageAttach);
  const [expanded, setExpanded] = useState(false);

  const expectedType: GeneratedAd["type"] = agentId === "banan" ? "image" : "video";
  const isVideoAgent =
    agentId === "vid" ||
    agentId === "higgsfield" ||
    agentId === "higgsfield_cli" ||
    agentId === "agent_v";

  const assets = useMemo(() => {
    if (!activeSessionId) return [] as GeneratedAd[];
    const activeIdx = sessionList.findIndex((s) => s.id === activeSessionId);
    const activeMeta = activeIdx >= 0 ? sessionList[activeIdx] : null;
    // Session list is newest-first; the next-newer session's createdAt is the
    // upper timestamp bound for assets produced during `activeMeta`.
    const lowerTs = activeMeta ? Date.parse(activeMeta.createdAt) : null;
    const upperTs =
      activeIdx > 0 ? Date.parse(sessionList[activeIdx - 1].createdAt) : Number.POSITIVE_INFINITY;

    const filtered = generatedAds.filter((ad) => {
      if (ad.type !== expectedType) return false;
      const meta = ad.metadata as Record<string, unknown> | undefined;
      // Kling and Higgsfield both produce `type: "video"`. Use the `generator`
      // tag to keep each agent's strip showing only its own outputs even when
      // sessions overlap in time or the user toggles between Vid/Vidzz chats.
      if (agentId === "higgsfield" && meta?.generator !== "higgsfield") return false;
      if (agentId === "higgsfield_cli" && meta?.generator !== "higgsfield_cli") return false;
      if (agentId === "agent_v" && meta?.generator !== "heygen") return false;
      if (
        agentId === "vid" &&
        (meta?.generator === "higgsfield" ||
          meta?.generator === "higgsfield_cli" ||
          meta?.generator === "heygen")
      ) {
        return false;
      }
      const tagged = typeof meta?.sessionId === "string" ? (meta.sessionId as string) : null;
      if (tagged) return tagged === activeSessionId;
      // Legacy fallback: try to place untagged assets by creation time.
      if (!lowerTs) return false;
      const createdRaw = (ad.result as { created_at?: string } | undefined)?.created_at;
      const createdTs = createdRaw ? Date.parse(createdRaw) : NaN;
      if (!Number.isFinite(createdTs)) return false;
      return createdTs >= lowerTs && createdTs < upperTs;
    });

    // Defensive URL-based dedup. Different completion paths (poll vs.
    // streaming-history rebuild) can stamp the same image with different
    // `result.id`s before the store-level dedup catches them, so the strip
    // would otherwise render duplicate thumbnails for one underlying asset.
    const seenUrls = new Set<string>();
    const out: GeneratedAd[] = [];
    for (const ad of filtered) {
      const url = ad.type === "image" ? ad.result.output : ad.result.video_output;
      if (!url || seenUrls.has(url)) continue;
      seenUrls.add(url);
      out.push(ad);
    }
    return out;
  }, [agentId, generatedAds, sessionList, activeSessionId, expectedType]);

  // For the video agents: surface the reference images that the agent
  // actually consumed to produce the videos in this session. Sources:
  //   1. Each video's `metadata.sourceImageUrl` — direct image-to-video input
  //      (Vid / Kling).
  //   2. Each video's `metadata.sourceMedias[]` — source inputs fed into the
  //      managed agent. The ecommerce-video path stamps only the product image.
  //   3. Banan-produced element/text-frame images that share a
  //      `sourceVideoScriptAngle` with any video in this session — Vid only.
  //      Vidzz dispatches don't carry script-angle lineage.
  const referenceImages = useMemo(() => {
    if (!isVideoAgent || assets.length === 0) {
      return [] as { url: string; label: string }[];
    }
    const angles = new Set<string>();
    const directUrls: string[] = [];
    const stampedMedias: { url: string; label: string }[] = [];
    for (const ad of assets) {
      const meta = ad.metadata as Record<string, unknown> | undefined;
      const angle = typeof meta?.sourceVideoScriptAngle === "string" ? meta.sourceVideoScriptAngle : null;
      if (angle) angles.add(angle);
      const src = typeof meta?.sourceImageUrl === "string" ? meta.sourceImageUrl : null;
      if (src) directUrls.push(src);
      const medias = Array.isArray(meta?.sourceMedias)
        ? (meta?.sourceMedias as Array<{ url?: unknown; label?: unknown }>)
        : null;
      if (medias) {
        for (const m of medias) {
          if (typeof m?.url !== "string") continue;
          stampedMedias.push({
            url: m.url,
            label: typeof m.label === "string" && m.label ? m.label : "ref",
          });
        }
      }
    }

    const seen = new Set<string>();
    const out: { url: string; label: string }[] = [];
    for (const url of directUrls) {
      if (seen.has(url)) continue;
      seen.add(url);
      out.push({ url, label: "input" });
    }
    for (const ref of stampedMedias) {
      if (seen.has(ref.url)) continue;
      seen.add(ref.url);
      out.push(ref);
    }
    if (agentId === "vid") {
      for (const ad of generatedAds) {
        if (ad.type !== "image") continue;
        const meta = ad.metadata as Record<string, unknown> | undefined;
        if (!meta) continue;
        const angle = typeof meta.sourceVideoScriptAngle === "string" ? meta.sourceVideoScriptAngle : null;
        if (!angle || !angles.has(angle)) continue;
        const url = ad.result.output;
        if (!url || seen.has(url)) continue;
        const ugcEl = meta.ugcElement as { elementKey?: string; kind?: string } | undefined;
        const ugcTf = meta.ugcTextFrame as { assignedBeat?: string } | undefined;
        if (!ugcEl?.elementKey && !ugcTf?.assignedBeat) continue;
        seen.add(url);
        const label = ugcEl?.elementKey
          ? ugcEl.elementKey
          : ugcTf?.assignedBeat
            ? `text · ${ugcTf.assignedBeat}`
            : "ref";
        out.push({ url, label });
      }
    }
    return out;
  }, [agentId, isVideoAgent, assets, generatedAds]);

  if (!activeSessionId) return null;

  const label = agentId === "banan" ? "Images" : "Videos";
  const EmptyIcon = agentId === "banan" ? ImageIcon : Film;
  const canExpand = assets.length > 0 || referenceImages.length > 0;

  return (
    <div className="shrink-0 flex flex-col bg-card/40 border-b border-border/10">
      <div className="flex items-center gap-2 px-3 py-1.5">
        <button
          type="button"
          onClick={() => canExpand && setExpanded((v) => !v)}
          disabled={!canExpand}
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${label.toLowerCase()}` : `Expand ${label.toLowerCase()}`}
          className={cn(
            "flex items-center gap-1 shrink-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground rounded-md px-1 py-0.5 transition-colors",
            canExpand && "hover:bg-muted/40 hover:text-foreground cursor-pointer",
            !canExpand && "cursor-default",
          )}
        >
          <EmptyIcon className="w-3 h-3" />
          <span>{label}</span>
          <span className="text-muted-foreground/60 normal-case font-normal">
            ({assets.length})
          </span>
          {canExpand && (
            <ChevronDown
              className={cn(
                "w-3 h-3 transition-transform",
                expanded ? "rotate-180" : "rotate-0",
              )}
              aria-hidden="true"
            />
          )}
        </button>

        {assets.length === 0 ? (
          <div className="flex-1 text-[10px] text-muted-foreground/60 italic truncate">
            No {label.toLowerCase()} in this session yet.
          </div>
        ) : !expanded ? (
          <div className="flex-1 flex items-center gap-1 overflow-x-auto hidden-scrollbar">
            {assets.map((ad, idx) => (
              <AssetThumb
                key={`${ad.result.id ?? idx}`}
                ad={ad}
                idx={idx}
                onAttach={requestChatImageAttach}
              />
            ))}
          </div>
        ) : null}
      </div>

      {expanded && assets.length > 0 && (
        <div className="px-3 pb-2 max-h-48 overflow-y-auto">
          <div className="flex flex-wrap gap-1">
            {assets.map((ad, idx) => (
              <AssetThumb
                key={`${ad.result.id ?? idx}`}
                ad={ad}
                idx={idx}
                onAttach={requestChatImageAttach}
              />
            ))}
          </div>
        </div>
      )}

      {isVideoAgent && referenceImages.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-1.5 border-t border-border/10">
          <div className="flex items-center gap-1 shrink-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground rounded-md px-1 py-0.5">
            <ImageIcon className="w-3 h-3" />
            <span>References</span>
            <span className="text-muted-foreground/60 normal-case font-normal">
              ({referenceImages.length})
            </span>
          </div>

          {!expanded ? (
            <div className="flex-1 flex items-center gap-1 overflow-x-auto hidden-scrollbar">
              {referenceImages.map((ref) => (
                <RefThumb
                  key={ref.url}
                  url={ref.url}
                  label={ref.label}
                  onAttach={requestChatImageAttach}
                />
              ))}
            </div>
          ) : (
            <div className="flex-1 max-h-48 overflow-y-auto">
              <div className="flex flex-wrap gap-1">
                {referenceImages.map((ref) => (
                  <RefThumb
                    key={ref.url}
                    url={ref.url}
                    label={ref.label}
                    onAttach={requestChatImageAttach}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

type AttachPayload = {
  url: string;
  filename: string;
  mediaType: string;
  kind?: "image" | "video";
};

function AssetThumb({
  ad,
  idx,
  onAttach,
}: {
  ad: GeneratedAd;
  idx: number;
  onAttach: (payload: AttachPayload) => void;
}) {
  const key = `${ad.result.id ?? idx}`;
  if (ad.type === "image") {
    const url = ad.result.output;
    if (!url) return null;
    return (
      <button
        type="button"
        onClick={() =>
          onAttach({
            url,
            filename: `session-image-${key}.jpg`,
            mediaType: "image/jpeg",
            kind: "image",
          })
        }
        className="relative w-10 h-10 shrink-0 rounded-md overflow-hidden bg-muted/30 border border-border/30 hover:border-primary/60 transition-colors"
        title="Send to chat"
        aria-label="Send image to chat"
      >
        <img src={url} alt="" className="w-full h-full object-cover" />
      </button>
    );
  }
  const thumb = ad.result.video_thumbnail;
  const videoUrl = ad.result.video_output;
  if (!videoUrl) return null;
  return (
    <button
      type="button"
      onClick={() =>
        onAttach({
          url: videoUrl,
          filename: `session-video-${key}.mp4`,
          mediaType: "video/mp4",
          kind: "video",
        })
      }
      className="relative w-10 h-10 shrink-0 rounded-md overflow-hidden bg-muted/30 border border-border/30 hover:border-primary/60 transition-colors"
      title="Send to chat"
      aria-label="Send video to chat"
    >
      {thumb ? (
        <img src={thumb} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-muted/50">
          <Film className="w-3.5 h-3.5 text-muted-foreground" />
        </div>
      )}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-4 h-4 rounded-full bg-black/50 flex items-center justify-center">
          <Film className="w-2.5 h-2.5 text-white" />
        </div>
      </div>
    </button>
  );
}

function RefThumb({
  url,
  label,
  onAttach,
}: {
  url: string;
  label: string;
  onAttach: (payload: AttachPayload) => void;
}) {
  return (
    <button
      type="button"
      onClick={() =>
        onAttach({
          url,
          filename: `session-ref-${label || "image"}.jpg`,
          mediaType: "image/jpeg",
          kind: "image",
        })
      }
      className="relative w-10 h-10 shrink-0 rounded-md overflow-hidden bg-muted/30 border border-border/30 hover:border-primary/60 transition-colors"
      title={label ? `Reference · ${label}` : "Reference"}
      aria-label={label ? `Send reference ${label} to chat` : "Send reference to chat"}
    >
      <img src={url} alt="" className="w-full h-full object-cover" />
    </button>
  );
}
